/**
 * The Fullscreen API with the vendor names folded together — Safari still ships
 * only the `webkit` ones, and an embedded frame can refuse it outright.
 */

export interface FullscreenDocument {
  fullscreenEnabled?: boolean;
  webkitFullscreenEnabled?: boolean;
  fullscreenElement?: Element | null;
  webkitFullscreenElement?: Element | null;
  exitFullscreen?: () => Promise<void>;
  webkitExitFullscreen?: () => Promise<void> | void;
}

export interface FullscreenTarget {
  requestFullscreen?: (options?: FullscreenOptions) => Promise<void>;
  webkitRequestFullscreen?: () => Promise<void> | void;
}

export interface FullscreenApi {
  supported: boolean;
  events: string[];
  element: () => Element | null;
  request: (target: FullscreenTarget) => Promise<void>;
  exit: () => Promise<void>;
}

/** Both names have to be listened for while Safari is still prefixed. */
export const FULLSCREEN_EVENTS = ['fullscreenchange', 'webkitfullscreenchange'];

export function fullscreenApi(doc: FullscreenDocument): FullscreenApi {
  // `fullscreenEnabled` is present but false inside an iframe without
  // allow="fullscreen", which is a refusal rather than a missing feature.
  const supported = Boolean(doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled);

  return {
    supported,
    events: FULLSCREEN_EVENTS,

    element: () => doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null,

    request: async (target) => {
      const request = target.requestFullscreen ?? target.webkitRequestFullscreen;
      if (request) await request.call(target);
    },

    exit: async () => {
      const exit = doc.exitFullscreen ?? doc.webkitExitFullscreen;
      if (exit) await exit.call(doc);
    },
  };
}
